import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Zap, Radar, Mail, MessageSquare, Clock, XCircle, CheckCircle2, Send, MoreHorizontal, Bell, Repeat, User } from "lucide-react";
import { toast } from "sonner";

const KIND_META = {
  quote_follow_up:     { text: "Sales",         cls: "bg-orange-100 text-[#E04F00] border-orange-200" },
  review_request:      { text: "Review",        cls: "bg-emerald-100 text-emerald-700 border-emerald-200" },
  reactivation:        { text: "Reactivation",  cls: "bg-indigo-100 text-indigo-700 border-indigo-200" },
  appointment_reminder:{ text: "Reminder",      cls: "bg-sky-100 text-sky-700 border-sky-200" },
  reengagement:        { text: "Re-engagement", cls: "bg-fuchsia-100 text-fuchsia-700 border-fuchsia-200" },
};

const STATUS_META = {
  scheduled:   { icon: Clock,        color: "text-orange-600", cls: "bg-orange-50 border-orange-200" },
  completed:   { icon: CheckCircle2, color: "text-emerald-600", cls: "bg-emerald-50 border-emerald-200" },
  canceled:    { icon: XCircle,      color: "text-rose-500",   cls: "bg-zinc-50 border-zinc-200" },
  dispatching: { icon: Zap,          color: "text-amber-600",  cls: "bg-amber-50 border-amber-200" },
};

export default function Automations() {
  const nav = useNavigate();
  const [data, setData] = useState({ tasks: [], counts_by_status: {}, counts_by_kind_scheduled: {} });
  const [status, setStatus] = useState("scheduled");
  const [kind, setKind] = useState("all");
  const [loading, setLoading] = useState(true);
  const [dispatching, setDispatching] = useState(false);
  const [editTask, setEditTask] = useState(null);
  const [editForm, setEditForm] = useState({ scheduled_for: "", dispatched_body: "" });

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = { status };
      if (kind !== "all") params.kind = kind;
      const { data } = await api.get("/automations", { params });
      setData(data);
    } catch { toast.error("Failed to load automations"); }
    finally { setLoading(false); }
  }, [status, kind]);
  useEffect(() => { load(); }, [load]);

  const dispatchAll = async () => {
    setDispatching(true);
    try {
      const { data } = await api.post("/automations/dispatch-now");
      const sent = data.results.filter((r) => r.result === "sent").length;
      const canceled = data.results.filter((r) => r.result === "canceled").length;
      toast.success(`${sent} sent · ${canceled} canceled · ${data.processed - sent - canceled} other`);
      load();
    } catch { toast.error("Dispatch failed"); }
    finally { setDispatching(false); }
  };

  // Generic actions (work for lead, customer, and job tasks alike)
  const cancelTask = async (taskId) => {
    try { await api.post(`/tasks/${taskId}/cancel`); toast.success("Canceled"); load(); }
    catch { toast.error("Cancel failed"); }
  };
  const snoozeTask = async (taskId, hours = 24) => {
    try { const { data } = await api.post(`/tasks/${taskId}/snooze`, null, { params: { hours } }); toast.success(`Snoozed ${hours}h`); load(); }
    catch { toast.error("Snooze failed"); }
  };
  const dispatchNow = async (taskId) => {
    try {
      const { data } = await api.post(`/tasks/${taskId}/dispatch-now`);
      const r = data?.result || {};
      // Backend reports mocked=true only when provider=mock (preview). In
      // live mode the row is persisted via the provider and this flag is
      // false — surface the real outcome to the operator.
      const label = r.result === "sent" ? (r.mocked ? "Sent (test mode)" : "Sent") : "Dispatched";
      toast.success(label); load();
    }
    catch { toast.error("Dispatch failed"); }
  };
  const openEdit = (t) => {
    setEditTask(t);
    setEditForm({
      scheduled_for: t.scheduled_for ? new Date(t.scheduled_for).toISOString().slice(0, 16) : "",
      dispatched_body: t.override_body || t.dispatched_body || "",
    });
  };
  const saveEdit = async (e) => {
    e.preventDefault();
    try {
      const payload = {};
      if (editForm.scheduled_for) payload.scheduled_for = new Date(editForm.scheduled_for).toISOString();
      if (editForm.dispatched_body !== undefined) payload.dispatched_body = editForm.dispatched_body;
      await api.patch(`/tasks/${editTask.id}`, payload);
      toast.success("Task updated");
      setEditTask(null);
      load();
    } catch { toast.error("Update failed"); }
  };

  const c = data.counts_by_status || {};
  const k = data.counts_by_kind_scheduled || {};

  return (
    <div className="p-8">
      <div className="flex items-center justify-between mb-8 flex-wrap gap-3">
        <div>
          <div className="text-xs font-mono-accent uppercase tracking-widest text-[#FF5A00] mb-1">Ops</div>
          <h1 className="font-heading text-4xl font-bold">Automation queue</h1>
          <p className="text-sm text-zinc-500 mt-1">Every scheduled follow-up, review request, appointment reminder, and re-engagement — across all leads and customers.</p>
        </div>
        <div className="flex items-center gap-2">
          <div className="flex items-center gap-2 px-2.5 py-1.5 rounded-full bg-emerald-50 border border-emerald-200">
            <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 animate-pulse"/>
            <span className="text-[10px] uppercase font-mono-accent tracking-widest text-emerald-700">Scheduler live · 60s</span>
          </div>
          <Button onClick={dispatchAll} disabled={dispatching || (c.scheduled || 0) === 0} className="bg-[#FF5A00] hover:bg-[#E04F00] text-white btn-lift" data-testid="automations-dispatch-all-btn">
            <Zap className="w-4 h-4 mr-1"/> {dispatching ? "Dispatching…" : "Dispatch all due"}
          </Button>
        </div>
      </div>

      {/* Overview stats */}
      <div className="grid grid-cols-2 lg:grid-cols-6 gap-3 mb-8">
        <StatCard label="Pending" value={c.scheduled || 0} accent="orange" testid="stat-scheduled"/>
        <StatCard label="Sent" value={c.completed || 0} accent="emerald" testid="stat-completed"/>
        <StatCard label="Canceled" value={c.canceled || 0} accent="zinc" testid="stat-canceled"/>
        <StatCard label="Reminders" value={k.appointment_reminder || 0} accent="sky" testid="stat-kind-reminder"/>
        <StatCard label="Re-engagement" value={k.reengagement || 0} accent="fuchsia" testid="stat-kind-reengagement"/>
        <StatCard label="Reviews" value={k.review_request || 0} accent="emerald" testid="stat-kind-review"/>
      </div>

      {/* Filters */}
      <div className="bg-white border border-zinc-200 rounded-xl card-crisp overflow-hidden">
        <div className="p-4 border-b border-zinc-100 flex items-center gap-3 flex-wrap">
          <div className="flex items-center gap-2">
            <span className="text-xs uppercase tracking-widest text-zinc-500 font-mono-accent">Status</span>
            <Select value={status} onValueChange={setStatus}>
              <SelectTrigger className="w-40" data-testid="automations-filter-status"><SelectValue/></SelectTrigger>
              <SelectContent>
                <SelectItem value="scheduled">Scheduled</SelectItem>
                <SelectItem value="completed">Completed</SelectItem>
                <SelectItem value="canceled">Canceled</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="flex items-center gap-2">
            <span className="text-xs uppercase tracking-widest text-zinc-500 font-mono-accent">Kind</span>
            <Select value={kind} onValueChange={setKind}>
              <SelectTrigger className="w-48" data-testid="automations-filter-kind"><SelectValue/></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All kinds</SelectItem>
                <SelectItem value="quote_follow_up">Sales follow-up</SelectItem>
                <SelectItem value="review_request">Review request</SelectItem>
                <SelectItem value="reactivation">Reactivation</SelectItem>
                <SelectItem value="appointment_reminder">Appointment reminder</SelectItem>
                <SelectItem value="reengagement">Re-engagement</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>

        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-zinc-50 text-xs uppercase text-zinc-500">
              <tr>
                <th className="text-left px-4 py-3">Recipient</th>
                <th className="text-left px-4 py-3">Kind</th>
                <th className="text-left px-4 py-3">Channel</th>
                <th className="text-left px-4 py-3">When</th>
                <th className="text-left px-4 py-3">Status</th>
                <th className="text-right px-4 py-3">Actions</th>
              </tr>
            </thead>
            <tbody>
              {loading && (
                <tr><td colSpan="6" className="text-center text-zinc-500 py-12">Loading…</td></tr>
              )}
              {!loading && data.tasks.length === 0 && (
                <tr><td colSpan="6" className="text-center text-zinc-500 py-12">No automations match the current filters.</td></tr>
              )}
              {data.tasks.map((t) => {
                const km = KIND_META[t.kind] || KIND_META.quote_follow_up;
                const sm = STATUS_META[t.status] || STATUS_META.scheduled;
                const StatusIcon = sm.icon;
                const ChannelIcon = t.channel === "sms" ? MessageSquare : Mail;
                const timeField = t.status === "completed" ? t.dispatched_at || t.completed_at
                  : t.status === "canceled" ? t.canceled_at
                  : t.scheduled_for;
                return (
                  <tr key={t.id} className="border-t border-zinc-100 hover:bg-zinc-50" data-testid={`automation-queue-row-${t.id}`}>
                    <td className="px-4 py-3">
                      {t.lead ? (
                        <button onClick={()=>nav(`/app/leads/${t.lead.id}`)} className="text-left" data-testid={`queue-lead-link-${t.lead.id}`}>
                          <div className="font-medium hover:text-[#FF5A00]">{t.lead.customer_name}</div>
                          <div className="text-xs text-zinc-500 capitalize">{(t.lead.status||"").replace(/_/g," ")} · {t.lead.service_requested || "—"}</div>
                        </button>
                      ) : t.customer ? (
                        <button onClick={()=>nav(`/app/customers`)} className="text-left" data-testid={`queue-customer-link-${t.customer.id}`}>
                          <div className="font-medium hover:text-[#FF5A00] flex items-center gap-1.5">
                            <User className="w-3.5 h-3.5 text-zinc-400"/>{t.customer.name || "Customer"}
                          </div>
                          <div className="text-xs text-zinc-500">
                            {t.customer.phone || t.customer.email || "—"}
                            {t.customer.sms_opted_out && <span className="ml-2 text-rose-500 font-mono-accent uppercase text-[9px]">opted out</span>}
                          </div>
                        </button>
                      ) : <span className="text-zinc-400">Unlinked</span>}
                    </td>
                    <td className="px-4 py-3">
                      <span className={`text-[10px] font-mono-accent uppercase tracking-widest px-1.5 py-0.5 rounded border ${km.cls}`}>{km.text}</span>
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-2 text-zinc-700">
                        <ChannelIcon className="w-3.5 h-3.5"/>
                        <span>{t.channel === "sms" ? "SMS" : "Email"}</span>
                        <span className="font-mono-accent text-xs text-zinc-500">· {t.step}</span>
                      </div>
                    </td>
                    <td className="px-4 py-3 font-mono-accent text-xs text-zinc-700">
                      {timeField ? new Date(timeField).toLocaleString() : "—"}
                    </td>
                    <td className="px-4 py-3">
                      <div className={`inline-flex items-center gap-1 text-xs px-2 py-1 rounded-md border ${sm.cls} ${sm.color}`}>
                        <StatusIcon className="w-3 h-3"/>
                        <span className="capitalize">{t.status}</span>
                        {t.retry_count > 0 && <span className="font-mono-accent">· {t.retry_count}/3</span>}
                      </div>
                      {t.cancel_reason && (
                        <div className="text-[10px] text-zinc-500 mt-1 truncate max-w-[220px]" title={t.cancel_reason}>
                          {t.cancel_reason.replace(/_/g," ")}
                        </div>
                      )}
                    </td>
                    <td className="px-4 py-3 text-right">
                      {t.status === "scheduled" && (
                        <div className="inline-flex items-center gap-1">
                          <button
                            onClick={()=>dispatchNow(t.id)}
                            className="text-xs px-2 py-1 rounded border border-orange-200 bg-orange-50 text-[#E04F00] hover:bg-orange-100 inline-flex items-center gap-1"
                            data-testid={`queue-dispatch-${t.id}`}
                            title="Send now"
                          ><Send className="w-3 h-3"/> Send</button>
                          <button
                            onClick={()=>snoozeTask(t.id, 24)}
                            className="text-xs px-2 py-1 rounded border border-zinc-200 bg-white text-zinc-600 hover:bg-zinc-50 inline-flex items-center gap-1"
                            data-testid={`queue-snooze-${t.id}`}
                            title="Snooze 24 hours"
                          ><Clock className="w-3 h-3"/> +24h</button>
                          <button
                            onClick={()=>openEdit(t)}
                            className="text-xs px-2 py-1 rounded border border-zinc-200 bg-white text-zinc-600 hover:bg-zinc-50"
                            data-testid={`queue-edit-${t.id}`}
                          >Edit</button>
                          <button
                            onClick={()=>cancelTask(t.id)}
                            className="text-xs px-2 py-1 rounded border border-zinc-200 bg-white text-rose-600 hover:bg-rose-50"
                            data-testid={`queue-cancel-${t.id}`}
                          >Cancel</button>
                        </div>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      <div className="mt-4 text-[10px] font-mono-accent text-zinc-400">
        SMS delivery follows the tenant setting in Settings → SMS automations.
      </div>

      {/* Edit dialog */}
      <Dialog open={!!editTask} onOpenChange={(v)=>!v && setEditTask(null)}>
        <DialogContent className="sm:max-w-md" data-testid="queue-edit-dialog">
          <DialogHeader><DialogTitle className="font-heading text-2xl">Edit scheduled task</DialogTitle></DialogHeader>
          {editTask && (
            <form onSubmit={saveEdit} className="space-y-4">
              <div>
                <label className="text-xs uppercase tracking-widest text-zinc-500 font-mono-accent">Scheduled for</label>
                <Input type="datetime-local" value={editForm.scheduled_for} onChange={(e)=>setEditForm({...editForm, scheduled_for: e.target.value})} data-testid="queue-edit-scheduled-for"/>
              </div>
              <div>
                <label className="text-xs uppercase tracking-widest text-zinc-500 font-mono-accent">Override message body (optional)</label>
                <textarea rows={4} className="w-full border border-zinc-200 rounded p-2 text-sm" value={editForm.dispatched_body} onChange={(e)=>setEditForm({...editForm, dispatched_body: e.target.value})} data-testid="queue-edit-body"/>
                <p className="text-[10px] text-zinc-500 mt-1">Blank keeps the template default.</p>
              </div>
              <DialogFooter>
                <Button type="button" variant="outline" onClick={()=>setEditTask(null)}>Cancel</Button>
                <Button type="submit" className="bg-[#FF5A00] hover:bg-[#E04F00] text-white" data-testid="queue-edit-save">Save</Button>
              </DialogFooter>
            </form>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}

function StatCard({ label, value, accent, testid }) {
  const map = {
    orange:  "text-[#FF5A00] bg-orange-50 border-orange-200",
    emerald: "text-emerald-700 bg-emerald-50 border-emerald-200",
    zinc:    "text-zinc-700 bg-zinc-50 border-zinc-200",
    indigo:  "text-indigo-700 bg-indigo-50 border-indigo-200",
    sky:     "text-sky-700 bg-sky-50 border-sky-200",
    fuchsia: "text-fuchsia-700 bg-fuchsia-50 border-fuchsia-200",
  };
  return (
    <div className={`rounded-xl p-4 border ${map[accent] || map.zinc}`} data-testid={testid}>
      <div className="text-[10px] uppercase font-mono-accent tracking-widest opacity-70">{label}</div>
      <div className="font-heading text-3xl font-bold mt-1">{value}</div>
    </div>
  );
}
